import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Database, Shield, LogOut, BarChart3, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployee } from '@/lib/pulse/useEmployee';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { RoleSwitcher } from '@/components/layout/RoleSwitcher';
import { ActiveSurveyBanner } from '@/components/employee/ActiveSurveyBanner';
import { CharlesProvider } from '@/contexts/CharlesContext';
import { CharlesOrb } from '@/components/employee/CharlesOrb';
import { supabase } from '@/lib/supabase';
import { usePlatformAdmin } from '@/lib/platform/usePlatformAdmin';

const nav = [
  { to: '/app/employee', label: 'Today', icon: Heart },
  { to: '/app/employee/charles', label: 'Talk to Charles', icon: MessageCircle },
  { to: '/app/me', label: 'Your Data', icon: Database },
  { to: '/app/trust', label: 'Trust Center', icon: Shield },
];

const adminNavItem = { to: '/app/admin', label: 'Admin', icon: BarChart3 };
const platformNavItem = { to: '/app/platform/tenants', label: 'Platform', icon: Building2 };

export function EmployeeLayout() {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const employee = useEmployee();
  const [isAdmin, setIsAdmin] = useState(false);
  const isPlatformAdmin = usePlatformAdmin();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('pulse_org_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (!cancelled) setIsAdmin(!!data);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const mobileNav = [
    ...nav,
    ...(isAdmin ? [adminNavItem] : []),
    ...(isPlatformAdmin === true ? [platformNavItem] : []),
  ];

  if (employee.status === 'no_invite') return <Navigate to="/auth/no-invite" replace />;
  if (employee.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (employee.status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-destructive">{employee.message}</p>
      </div>
    );
  }

  return (
    <CharlesProvider>
      <div className="min-h-screen flex w-full bg-background">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:flex flex-col w-60 border-r border-border bg-card">
          <div className="px-5 py-6 border-b border-border">
            <h1 className="text-lg font-heading font-semibold">CultureLens</h1>
            <p className="text-xs text-muted-foreground">Employee</p>
          </div>
          <RoleSwitcher current="employee" />
          <nav className="flex-1 p-3 space-y-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={async () => {
                await signOut();
                navigate('/auth/signin');
              }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </aside>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border flex justify-around py-2 safe-area-inset-bottom">
          {mobileNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 px-3 py-1 text-[11px]',
                  isActive ? 'text-primary' : 'text-muted-foreground',
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label.split(' ')[0]}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 overflow-auto pb-20 md:pb-0">
          {employee.status === 'ready' && <ActiveSurveyBanner orgId={employee.orgId} />}
          <Outlet />
        </main>

        <CharlesOrb />
      </div>
    </CharlesProvider>
  );
}
